import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Search, Edit2, Trash2, Eye, EyeOff, GripVertical, Image, AlertTriangle } from 'lucide-react';
import { Banner } from '@/types/banner';
import { bannerApi } from '@/services/bannerApi';
import { BANNER_ASPECT_RATIO, getEffectivePlacement } from '@shared/bannerAspectRatio';
import toast from 'react-hot-toast';

const gridSizeLabels: Record<string, string> = {
  large: 'Büyük',
  medium: 'Orta',
  small: 'Küçük',
};

const placementLabels: Record<string, string> = {
  hero: 'Hero',
  featured: 'Vitrin Büyük',
  small: 'Vitrin Küçük',
};

const placementBadgeClass: Record<string, string> = {
  hero: 'bg-rose-100 text-rose-700',
  featured: 'bg-amber-100 text-amber-700',
  small: 'bg-slate-100 text-slate-700',
};

const linkTypeLabels: Record<string, string> = {
  category: 'Kategori',
  product: 'Ürün',
  page: 'Sayfa',
  url: 'Harici URL',
  none: 'Yok',
};

export const BannersList: React.FC = () => {
  const navigate = useNavigate();
  const [banners, setBanners] = useState<Banner[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [aspectMismatch, setAspectMismatch] = useState<Record<string, boolean>>({});

  const checkAspectRatio = (banner: Banner, img: HTMLImageElement) => {
    if (aspectMismatch[banner.id] !== undefined) return;
    const actual = img.naturalWidth / img.naturalHeight;
    const expected = BANNER_ASPECT_RATIO[getEffectivePlacement(banner)];
    const mismatch = Math.abs(actual - expected) / expected > 0.03;
    setAspectMismatch((prev) => ({ ...prev, [banner.id]: mismatch }));
  };

  useEffect(() => {
    loadBanners();
  }, []);

  const loadBanners = async () => {
    try {
      setLoading(true);
      const data = await bannerApi.getAllBanners();
      setBanners(data);
    } catch (error: any) {
      console.error('Banner yüklenirken hata:', error);
      toast.error(error.message || 'Bannerlar yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string, title: string) => {
    if (window.confirm(`"${title || 'Bu banner'}"'ı silmek istediğinize emin misiniz?`)) {
      try {
        await bannerApi.deleteBanner(id);
        toast.success('Banner silindi!');
        loadBanners();
      } catch (error: any) {
        toast.error(error.message || 'Banner silinemedi');
      }
    }
  };

  const handleToggleActive = async (banner: Banner) => {
    try {
      await bannerApi.updateBanner(banner.id, { isActive: !banner.isActive });
      toast.success(banner.isActive ? 'Banner deaktif edildi' : 'Banner aktif edildi');
      loadBanners();
    } catch (error: any) {
      toast.error(error.message || 'Durum güncellenemedi');
    }
  };

  const handleMoveUp = async (index: number) => {
    if (index === 0) return;
    const newBanners = [...banners];
    [newBanners[index - 1], newBanners[index]] = [newBanners[index], newBanners[index - 1]];
    const orders = newBanners.map((b, i) => ({ id: b.id, order: i + 1 }));
    try {
      await bannerApi.reorderBanners(orders);
      setBanners(newBanners);
      toast.success('Sıralama güncellendi');
    } catch (error: any) {
      toast.error(error.message || 'Sıralama güncellenemedi');
    }
  };

  const handleMoveDown = async (index: number) => {
    if (index === banners.length - 1) return;
    const newBanners = [...banners];
    [newBanners[index], newBanners[index + 1]] = [newBanners[index + 1], newBanners[index]];
    const orders = newBanners.map((b, i) => ({ id: b.id, order: i + 1 }));
    try {
      await bannerApi.reorderBanners(orders);
      setBanners(newBanners);
      toast.success('Sıralama güncellendi');
    } catch (error: any) {
      toast.error(error.message || 'Sıralama güncellenemedi');
    }
  };

  const filteredBanners = banners.filter((banner) =>
    (banner.title || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (banner.subtitle || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">🖼️ Banner Yönetimi</h1>
          <p className="text-gray-600 mt-1">
            Toplam {filteredBanners.length} banner
          </p>
        </div>
        <button
          onClick={() => navigate('/admin/banners/new')}
          className="inline-flex items-center gap-2 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition"
        >
          <Plus size={20} />
          Yeni Banner
        </button>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
        <input
          type="text"
          placeholder="Banner ara..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        />
      </div>

      {/* Loading */}
      {loading && (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-blue-500" />
        </div>
      )}

      {/* Banner List */}
      {!loading && filteredBanners.length === 0 && (
        <div className="text-center py-12 text-gray-500">
          <Image size={48} className="mx-auto mb-4 opacity-50" />
          <p>Henüz banner eklenmemiş</p>
        </div>
      )}

      {!loading && filteredBanners.length > 0 && (
        <div className="space-y-4">
          {filteredBanners.map((banner, index) => (
            <div
              key={banner.id}
              className={`bg-white border rounded-xl overflow-hidden shadow-sm hover:shadow-md transition ${
                !banner.isActive ? 'opacity-60' : ''
              }`}
            >
              <div className="flex items-stretch">
                {/* Image Preview */}
                <div className="w-48 h-32 flex-shrink-0 relative">
                  {banner.imageUrl ? (
                    <img
                      src={banner.imageUrl}
                      alt={banner.title || 'Banner'}
                      className="w-full h-full object-cover"
                      onLoad={(e) => checkAspectRatio(banner, e.currentTarget)}
                    />
                  ) : (
                    <div className="w-full h-full bg-gray-100 flex items-center justify-center">
                      <Image size={32} className="text-gray-300" />
                    </div>
                  )}
                  {/* Grid Size Badge */}
                  <span className="absolute top-2 left-2 px-2 py-0.5 bg-black/60 text-white text-xs rounded-full">
                    {gridSizeLabels[banner.gridSize] || banner.gridSize}
                  </span>
                  {/* Aspect Ratio Mismatch Badge */}
                  {aspectMismatch[banner.id] && (
                    <span
                      className="absolute bottom-2 left-2 flex items-center gap-1 px-2 py-0.5 bg-orange-500/90 text-white text-xs rounded-full"
                      title="Görsel bu yerleşim için doğru orana kırpılmamış"
                    >
                      <AlertTriangle size={12} />
                      Oran uyumsuz
                    </span>
                  )}
                </div>

                {/* Content */}
                <div className="flex-1 p-4 flex items-center">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold text-gray-900">
                        {banner.title || <span className="text-gray-400 italic">Başlıksız banner</span>}
                      </h3>
                      <span
                        className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                          placementBadgeClass[getEffectivePlacement(banner)]
                        }`}
                      >
                        {placementLabels[getEffectivePlacement(banner)]}
                      </span>
                    </div>
                    {banner.subtitle && (
                      <p className="text-sm text-gray-500 mt-0.5">{banner.subtitle}</p>
                    )}
                    <div className="flex gap-3 mt-2 text-xs text-gray-400">
                      <span>Link: {linkTypeLabels[banner.linkType]}</span>
                      {banner.linkValue && <span>→ {banner.linkValue}</span>}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2 ml-4">
                    {/* Reorder */}
                    <div className="flex flex-col gap-0.5">
                      <button
                        onClick={() => handleMoveUp(index)}
                        disabled={index === 0}
                        className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30"
                        title="Yukarı taşı"
                      >
                        <GripVertical size={14} className="rotate-180" />
                      </button>
                      <button
                        onClick={() => handleMoveDown(index)}
                        disabled={index === filteredBanners.length - 1}
                        className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30"
                        title="Aşağı taşı"
                      >
                        <GripVertical size={14} />
                      </button>
                    </div>

                    {/* Toggle Active */}
                    <button
                      onClick={() => handleToggleActive(banner)}
                      className={`p-2 rounded-lg transition ${
                        banner.isActive
                          ? 'text-green-600 hover:bg-green-50'
                          : 'text-gray-400 hover:bg-gray-50'
                      }`}
                      title={banner.isActive ? 'Deaktif et' : 'Aktif et'}
                    >
                      {banner.isActive ? <Eye size={18} /> : <EyeOff size={18} />}
                    </button>

                    {/* Edit */}
                    <button
                      onClick={() => navigate(`/admin/banners/${banner.id}/edit`)}
                      className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition"
                      title="Düzenle"
                    >
                      <Edit2 size={18} />
                    </button>

                    {/* Delete */}
                    <button
                      onClick={() => handleDelete(banner.id, banner.title)}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition"
                      title="Sil"
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default BannersList;
